import { useState, useRef, useEffect } from "react";
import { LayoutChangeEvent, Platform } from "react-native";

/**
 * useContainerWidth is a hook that measures the width of container element
 *
 * On mobile, it uses the onLayout callback
 * On web, it uses ResizeObserver (https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver)
 *
 * The width is null until the container has been measured
 * Width updates automatically when the container resizes
 *
 * Usage:
 * - Attach `ref` to the container (required on web)
 * - Attach `onLayout` to the container (required on mobile)
 *
 * @returns {{
 *   width: number | null,
 *   onLayout: (e: LayoutChangeEvent) => void,
 *   ref: React.RefObject<any>
 * }}
 *
 */

const MEDIUM = 870;

export const useContainerWidth = () => {
  const [width, setWidth] = useState<number | null>(null);
  const ref = useRef<any>(null);

  const wasAboveRef = useRef<boolean | null>(null);

  const maybeUpdate = (nextWidth: number) => {
    const isAbove = nextWidth >= MEDIUM;

    // First measurement
    if (wasAboveRef.current === null) {
      wasAboveRef.current = isAbove;
      setWidth(nextWidth);
      return;
    }

    // Only update when crossing 767
    if (wasAboveRef.current !== isAbove) {
      wasAboveRef.current = isAbove;
      setWidth(nextWidth);
    }
  };

  const onLayout = (e: LayoutChangeEvent) => {
    maybeUpdate(e.nativeEvent.layout.width);
  };

  useEffect(() => {
    if (Platform.OS !== "web" || !ref.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        maybeUpdate(entry.contentRect.width);
      }
    });

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return { width, onLayout, ref };
};
